import clsx from "clsx";
import {
  UseControllerProps,
  useController,
  useFormContext,
} from "react-hook-form";

import FormInputErrorLabel from "./FormInputErrorLabel";
import styles from "./FormInput.module.scss";
import Input, { InputProps } from "../support/Input";
import BodyText from "../support/BodyText";

type ReactHookFormRules = UseControllerProps["rules"];

interface FormInputProps extends InputProps {
  /* Name of the field in form state */
  fieldName: string;
  /* Text label of the input */
  label?: string;
  /* Validation rules of the input */
  rules?: ReactHookFormRules;
  /* Customized Error Message */
  renderCustomErrorComponent?: (errorMessage: string) => React.ReactNode;
  /* Class name of the container */
  containerClassName?: string;
  /* Class name for the label */
  labelClassName?: string;
  /* Whether input is required */
  required?: boolean;
}

function FormInput({
  fieldName,
  label,
  rules,
  renderCustomErrorComponent,
  readOnly,
  containerClassName,
  labelClassName,
  required,
  ...inputProps
}: FormInputProps) {
  const { setValue } = useFormContext();

  const {
    field: { onBlur, onChange, value },
    fieldState: { error, isTouched },
  } = useController({
    name: fieldName,
    rules,
  });

  const hasError = !!error && isTouched;

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const text = event.target.value;

    // Check whether text only contains whitespace
    if (text && !text.replace(/\s/g, "").length) {
      return;
    }

    onChange(text);
    setValue(fieldName, text);
  }

  return (
    <div className={clsx(styles.container, containerClassName)}>
      {label && (
        <BodyText
          as="label"
          color="body-1"
          className={clsx(styles.label, labelClassName)}
        >
          {label}
          {required && <span>{" *"}</span>}
        </BodyText>
      )}
      <Input
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...inputProps}
        value={value}
        onChange={handleChange}
        onBlur={onBlur}
        invalid={hasError}
        readOnly={readOnly}
        className={clsx(!hasError && styles.valid, readOnly && styles.readOnly)}
      />

      <FormInputErrorLabel
        htmlFor={fieldName}
        error={"Missing Required Value"}
      />
    </div>
  );
}

export default FormInput;
